import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { lecturesApi } from "@/api";
import type { UpsertLectureData } from "@/api";
import { QUERY_KEYS } from "@/constants";

/**
 * Darsning ma'ruza kontenti.
 *
 * Hali yaratilmagan bo'lsa backend 404 beradi va bu XATO EMAS — shuning
 * uchun `retry: false`: aks holda har bir yangi ma'ruza darsida uchta
 * behuda so'rov ketardi.
 */
export const useLecture = (lessonId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_LECTURE, lessonId],
    queryFn: () => lecturesApi.getByLesson(lessonId as number),
    enabled: !!lessonId,
    retry: false,
    select: res => res.data,
  });

const useInvalidateLecture = (lessonId: number, courseId?: number) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_LECTURE, lessonId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_READINESS, courseId] });
  };
};

export const useUpsertLecture = (lessonId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateLecture(lessonId, courseId);
  return useMutation({
    mutationFn: (data: UpsertLectureData) => lecturesApi.upsert(lessonId, data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useUploadLectureVideo = (lessonId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateLecture(lessonId, courseId);
  return useMutation({
    mutationFn: (vars: { file: File; onProgress?: (percent: number) => void }) =>
      lecturesApi.uploadVideo(lessonId, vars.file, vars.onProgress),
    onSuccess: () => {
      invalidate();
      toast.success(t("LESSON_CONTENT.VIDEO_UPLOADED"));
    },
  });
};

export const useAddLectureAttachments = (lessonId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateLecture(lessonId, courseId);
  return useMutation({
    mutationFn: (vars: { files: File[]; onProgress?: (percent: number) => void }) =>
      lecturesApi.addAttachments(lessonId, vars.files, vars.onProgress),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};
